import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { Alert, Form, Button } from 'react-bootstrap';
import { Input, FormActions, Select, HtmlEditor, Checkbox } from '../../common/components/formControls';
import validators from '../../common/services/validators';
import { withTranslation } from 'react-i18next';
import Localizations from '../localization';

// Resolve the applicable tag groups for the template being edited from the static catalog.
const getApplicableGroups = (tagCatalog, templateName, selectedTypeId, messageTemplateTypes) => {
	if (!tagCatalog || !tagCatalog.groups || tagCatalog.groups.length === 0) {
		return [];
	}
	const { groups, templateGroups = {}, defaultGroups = [] } = tagCatalog;

	// Determine the template's description string (== template Name / templateGroups key).
	let description = templateName; // existing template: initialValues.name is the description
	if (!description && selectedTypeId && messageTemplateTypes) {
		const selected = messageTemplateTypes.find(x => x.id === Number(selectedTypeId));
		description = selected && selected.displayName;
	}

	// known template type -> its mapped groups; otherwise (custom/ad-hoc/unmapped) -> default set
	const groupNames = (description && templateGroups[description]) || defaultGroups;

	// keep catalog order, de-duplicate, only groups that have tags
	const wanted = new Set(groupNames);
	return groups.filter(g => wanted.has(g.group));
};

const SettingsForm = ({ error, handleSubmit, pristine, submitting, isReadonly, initialValues, submitSucceeded,
	messageTemplateTypes, fromAddressTypes, tagCatalog, selectedTypeId, t }) => {
	let id = initialValues ? initialValues.id : 0;
	const isSystem = initialValues ? initialValues.isSystem : true;
	let allowSendFromCenter = initialValues ? initialValues.allowSendFromCenter : false;

	const applicableGroups = useMemo(
		() => getApplicableGroups(tagCatalog, initialValues && initialValues.name, selectedTypeId, messageTemplateTypes),
		[tagCatalog, initialValues, selectedTypeId, messageTemplateTypes]
	);

	// full catalog is added to the toolbar dropdown once; applicableGroups filters it on open
	const allTagGroups = (tagCatalog && tagCatalog.groups) || [];

	return (
		<Form horizontal onSubmit={handleSubmit}>
			{error && <Alert bsStyle='danger'>{error}</Alert>}
			{submitSucceeded && pristine && <Alert bsStyle='success'>{t('Common_UpdateSuccess')}</Alert>}
			{!id && isSystem && <Select name='typeId' disabled={isReadonly} required horizontal label={t('MessageTemplate_Name')} data={messageTemplateTypes} validate={[validators.required]} />}
			{id && isSystem && <Field name='name' component={Input} disabled horizontal type='text' label={t('MessageTemplate_Name')} />}
			{!isSystem && <Field name='name' component={Input} disabled={isReadonly} horizontal required type='text' label={t('MessageTemplate_Name')} validate={[validators.required]} />}
			{allowSendFromCenter && <Select name='fromAddressType' horizontal required hideEmptyOption label={t('MessageTemplate_FromAddressType')} data={fromAddressTypes} validate={validators.required} />}
			<Field name='subject' component={Input} disabled={isReadonly} horizontal required type='text' label={t('MessageTemplate_Subject')} validate={[validators.required]} />
			{id > 0 && <Localizations entityId={id} rootName={'messageTemplates'} propertyName={'Subject'} label={t('MessageTemplate_Subject')} resourceName={`MessageTemplate.${id}.Subject`} isHtml={false} isReadonly={isReadonly} />}
			<Field name='body' component={HtmlEditor} disabled={isReadonly} horizontal required type='text' label={t('MessageTemplate_Body')} validate={[validators.required]}
				tagsEnabled tagAllGroups={allTagGroups} applicableGroups={applicableGroups} comboLabel={t('MessageTemplate_MergeTags')} />
			{id > 0 && <Localizations entityId={id} rootName={'messageTemplates'} propertyName={'Body'} label={t('MessageTemplate_Body')} resourceName={`MessageTemplate.${id}.Body`} isHtml={true} isReadonly={isReadonly} />}
			<Field name='enabled' component={Checkbox} disabled={isReadonly} horizontal label={t('MessageTemplate_Enabled')} normalize={(v) => { return !!v }} />
			<FormActions horizontal>
				<Link to='/messageTemplates' className='btn btn-default'>{t('Common_BackToList')}</Link>
				<Button type='submit' disabled={isReadonly || pristine || submitting || isReadonly} bsStyle='primary'>{t('Common_Save')}</Button>
			</FormActions>
		</Form>
	);
};

const form = reduxForm({ form: 'messageTemplate', enableReinitialize: true })(withTranslation()(SettingsForm));

// expose the selected template type (for new system templates) so tag groups update as the type is picked
const selector = formValueSelector('messageTemplate');
export default connect(state => ({ selectedTypeId: selector(state, 'typeId') }))(form);
